import { parseDecimal, toKopecks } from './decimal'
import type {
  BasketItem,
  CalculatorDictionary,
  CalculatorRecipe,
  DictionaryIngredient,
  Quant,
} from '../types'

/** Цена за единицу квантовки: цена покупки, делённая на её объём. */
export const unitPrice = (item: BasketItem): number | null => {
  const amount = parseDecimal(item.amount)
  const price = parseDecimal(item.price)
  if (amount === null || price === null) return null
  // Нулевой объём означал бы деление на ноль.
  if (amount <= 0 || price < 0) return null
  return price / amount
}

export interface CostLine {
  ingredientId: number
  /** Название из справочника. null — ингредиента в справочнике не нашлось. */
  name: string | null
  /** Что показываем: название либо заглушка. По нему же и сортируем. */
  label: string
  quant: Quant | null
  /** Сколько нужно по рецепту, с учётом коэффициента пересчёта. */
  amount: number
  /** ₽ за единицу. null — продукта нет в корзине, цену не знаем. */
  unitPrice: number | null
  /** Стоимость строки в копейках. null — цены нет. */
  costKopecks: number | null
}

export interface CostBlock {
  blockId: number
  name: string | null
  lines: CostLine[]
  /** Сумма заполненных строк блока. */
  costKopecks: number
}

export interface RecipeCost {
  blocks: CostBlock[]
  /** Сумма по всем блокам — считаем только строки с ценой. */
  totalKopecks: number
  /** Строки без цены: пока они есть, итог неполный. */
  missing: CostLine[]
}

interface DictionaryIndex {
  blocks: Map<number, string>
  ingredients: Map<number, DictionaryIngredient>
}

export const indexDictionary = (
  dictionary: CalculatorDictionary | undefined
): DictionaryIndex => ({
  blocks: new Map(
    (dictionary?.blocks ?? []).map(block => [block.block_id, block.name])
  ),
  ingredients: new Map(
    (dictionary?.ingredients ?? []).map(item => [item.ingredient_id, item])
  ),
})

export const indexBasket = (
  items: readonly BasketItem[]
): Map<number, BasketItem> =>
  new Map(items.map(item => [item.ingredient_id, item]))

/**
 * Себестоимость рецепта: цена за единицу из корзины × количество из состава.
 *
 * Суммы копим в копейках: каждую строку округляем один раз, дальше складываем
 * целые. Иначе итог не сходится с суммой показанных строк.
 *
 * `coefficient` — поправка на другую форму или вес. 1 означает «как в рецепте».
 */
export const buildRecipeCost = (
  recipe: CalculatorRecipe,
  dictionary: DictionaryIndex,
  basket: Map<number, BasketItem>,
  coefficient = 1
): RecipeCost => {
  const missing: CostLine[] = []

  // Схема допускает отсутствие blocks и ingredients (у полей есть дефолты),
  // поэтому не полагаемся на то, что массивы приедут.
  const blocks = (recipe.blocks ?? []).map<CostBlock>(block => {
    const lines = (block.ingredients ?? [])
      .map<CostLine>(ingredient => {
        const known = dictionary.ingredients.get(ingredient.ingredient_id)
        const amount = (parseDecimal(ingredient.amount) ?? 0) * coefficient
        const basketItem = basket.get(ingredient.ingredient_id)
        const price = basketItem ? unitPrice(basketItem) : null

        return {
          ingredientId: ingredient.ingredient_id,
          name: known?.name ?? null,
          label: known?.name ?? `Продукт №${ingredient.ingredient_id}`,
          quant: known?.quant ?? null,
          amount,
          unitPrice: price,
          costKopecks: price === null ? null : toKopecks(price * amount),
        }
      })
      // Порядок ингредиентов внутри блока бэк не задаёт — сортируем сами, по
      // той же подписи, что видно в строке.
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'))

    lines.forEach(line => {
      if (line.costKopecks === null) missing.push(line)
    })

    return {
      blockId: block.block_id,
      name: dictionary.blocks.get(block.block_id) ?? null,
      lines,
      costKopecks: lines.reduce(
        (sum, line) => sum + (line.costKopecks ?? 0),
        0
      ),
    }
  })

  const uniqueMissing = [
    ...new Map(missing.map(line => [line.ingredientId, line])).values(),
  ]

  return {
    blocks,
    totalKopecks: blocks.reduce((sum, block) => sum + block.costKopecks, 0),
    missing: uniqueMissing,
  }
}

export interface LabourLine {
  roleId: number
  name: string
  hours: number
  /** ₽ за час. null — ставка для роли не задана. */
  rate: number | null
  costKopecks: number | null
}

export interface LabourCost {
  lines: LabourLine[]
  totalKopecks: number
  /** Роли с часами, но без ставки: итог по работе неполный. */
  missing: LabourLine[]
}

/**
 * Стоимость работы: часы × ставка по каждой роли. Как и у продуктов, каждую
 * строку округляем один раз и дальше складываем целые копейки.
 *
 * Часы не масштабируем вместе с формой: вдвое больший торт не украшают вдвое
 * дольше, и домножать их на коэффициент было бы выдумкой.
 */
export const buildLabourCost = (
  items: readonly { role_id: number; hours: string }[],
  roles: ReadonlyMap<number, string>,
  rates: ReadonlyMap<number, number>
): LabourCost => {
  const lines = items.map<LabourLine>(item => {
    const hours = parseDecimal(item.hours) ?? 0
    const rate = rates.get(item.role_id) ?? null
    return {
      roleId: item.role_id,
      name: roles.get(item.role_id) ?? `Роль №${item.role_id}`,
      hours,
      rate,
      costKopecks: rate === null ? null : toKopecks(rate * hours),
    }
  })

  return {
    lines,
    totalKopecks: lines.reduce((sum, line) => sum + (line.costKopecks ?? 0), 0),
    // Ноль часов — это «время ещё не прикинули», без ставки такая строка в
    // недостающие не просится: она и так ничего не добавит.
    missing: lines.filter(line => line.costKopecks === null && line.hours > 0),
  }
}

/** Стоимость килограмма изделия — при известном весе формы. */
export const costPerKg = (
  totalKopecks: number,
  weightGrams: number | null | undefined
): number | null => {
  if (!weightGrams || weightGrams <= 0) return null
  return Math.round((totalKopecks / weightGrams) * 1000)
}

/**
 * Стоимость одного изделия: итог рецепта, делённый на выход в штуках.
 *
 * Выход приходит с бэка полем `pieces` — его больше не вводят руками, поэтому
 * и считаем только когда оно заполнено. Изделий меньше одного не бывает.
 */
export const costPerPiece = (
  totalKopecks: number,
  pieces: number | null
): number | null => {
  if (pieces === null || !Number.isFinite(pieces) || pieces < 1) return null
  return Math.round(totalKopecks / pieces)
}
